import { createFileRoute, getRouteApi, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { AdminBillingPage, type BillingView } from '@/features/admin-billing'
// Through `queries`, not the feature indexes: a loader is eager by
// construction, and each of these panels brings a table with it.
import { billingSettingsQuery } from '@/features/admin-billing-settings/queries'
import { adminInvoicesQuery } from '@/features/admin-invoices/queries'
import { adminPlansQuery } from '@/features/admin-plans/queries'
import { PERMISSION_ADMIN } from '@/features/auth'

import { can } from '@/app/router/session'

// Inlined rather than imported from the feature: `validateSearch` runs eagerly,
// so importing a runtime value from the feature barrel here would pin the whole
// (otherwise lazy) page into the initial bundle. The type still comes from the
// feature and keeps this list honest.
const BILLING_VIEWS = ['invoices', 'plans', 'settings'] as const satisfies readonly BillingView[]

const searchSchema = z.object({
  view: z.enum(BILLING_VIEWS).default('invoices'),
})

export const Route = createFileRoute('/_auth/admin/billing')({
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    if (!can(context, PERMISSION_ADMIN)) throw redirect({ to: '/' })
  },
  loaderDeps: ({ search }) => ({ view: search.view }),
  // One screen, three panels, one visible at a time — so the loader preloads
  // the one the URL asks for and no more. Switching view re-runs it.
  // Branches rather than a lookup: each factory carries its own literal query
  // key type, and a map collapses the three into a union `ensureQueryData`
  // cannot narrow.
  loader: ({ context, deps }) => {
    if (deps.view === 'plans') return context.queryClient.ensureQueryData(adminPlansQuery())
    if (deps.view === 'settings') return context.queryClient.ensureQueryData(billingSettingsQuery())
    return context.queryClient.ensureQueryData(adminInvoicesQuery())
  },
  component: AdminBillingRoute,
})

const routeApi = getRouteApi('/_auth/admin/billing')

function AdminBillingRoute() {
  const { view } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  return (
    <AdminBillingPage
      view={view}
      onViewChange={(next) => void navigate({ search: { view: next } })}
    />
  )
}
